import React, { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import api, { apiError } from "@/api";
import { useAuth } from "@/context/AuthContext";
import { DataTable } from "@/components/DataTable";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ItemSearch } from "@/components/ItemSearch";
import { CustomerSearch } from "@/components/CustomerSearch";
import { fmtIDR, fmtDate, PAYMENT_STATUS, ORDER_STATUS, ITEM_STATUS } from "@/lib/format";
import { Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function Orders() {
  const { event, eventId } = useOutletContext();
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [editId, setEditId] = useState(null);
  const [customerId, setCustomerId] = useState("");
  const [note, setNote] = useState("");
  const [lines, setLines] = useState([]);
  const [fees, setFees] = useState([]);
  const [team, setTeam] = useState([]);
  const [picId, setPicId] = useState("");
  const canOrder = ["sales", "admin"].some((r) => user.roles.includes(r));
  const canPrice = ["finance", "admin"].some((r) => user.roles.includes(r));

  const load = async () => {
    const [o, c, cat, t] = await Promise.all([
      api.get(`/orders?event_id=${eventId}`), api.get("/customers"), api.get(`/events/${eventId}/catalog`), api.get("/users").catch(() => ({ data: [] })),
    ]);
    setOrders(o.data); setCustomers(c.data); setCatalog(cat.data); setTeam(t.data);
  };
  useEffect(() => { load(); }, [eventId]); // eslint-disable-line react-hooks/exhaustive-deps

  const openNew = () => { setEditId(null); setCustomerId(""); setNote(""); setFees([]); setPicId(user.id); setLines([{ key: Date.now(), catalog_item_id: "", qty: 1 }]); setOpen(true); };
  const openEdit = (o) => {
    setEditId(o.id); setCustomerId(o.customer_id); setNote(o.note || ""); setPicId(o.pic_id || user.id);
    setFees((o.extra_fees || []).map((f, idx) => ({ key: `fee-${idx}`, name: f.name, amount: f.amount })));
    setLines(o.items.map((i, idx) => ({ key: `${i.catalog_item_id}-${idx}`, catalog_item_id: i.catalog_item_id, qty: i.qty }))); setOpen(true);
  };

  const save = async () => {
    const items = lines.filter((l) => l.catalog_item_id).map((l) => ({ catalog_item_id: l.catalog_item_id, qty: Number(l.qty) }));
    if (!customerId || items.length === 0) return toast.error("Pilih customer & minimal 1 item");
    if (fees.some((f) => !f.name.trim() && Number(f.amount) > 0)) return toast.error("Isi nama biaya untuk setiap nominal yang diisi");
    const extra_fees = fees.filter((f) => f.name.trim()).map((f) => ({ name: f.name.trim(), amount: Number(f.amount) || 0 }));
    try {
      const payload = { event_id: eventId, customer_id: customerId, note, items, extra_fees, pic_id: picId || undefined };
      if (editId) await api.put(`/orders/${editId}`, payload);
      else await api.post("/orders", payload);
      toast.success(editId ? "Order diperbarui — status kembali menunggu pembayaran" : "Order dibuat");
      setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (o) => {
    const paid = o.payment_status === "valid" ? "\n\nPERHATIAN: order ini sudah dibayar — data pembayaran ikut terhapus." : "";
    if (!window.confirm(`Hapus order ${o.id.slice(0, 8)} milik ${o.customer_name} (${fmtIDR(o.total)})?${paid}`)) return;
    try { await api.delete(`/orders/${o.id}`); toast.success("Order dihapus"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const updatePrice = async (oid, idx, price) => {
    try { await api.patch(`/orders/${oid}/items/${idx}/price`, { sell_price: Number(price) }); toast.success("Harga jual diperbarui"); load(); setDetail(null); }
    catch (e) { toast.error(apiError(e)); }
  };

  const columns = [
    { key: "id", label: "Order ID", mono: true, render: (r) => <button className="font-mono text-blue-700 hover:underline" onClick={() => setDetail(r)}>{r.id.slice(0, 8)}</button>, raw: (r) => r.id.slice(0, 8) },
    { key: "created_at", label: "Tanggal", render: (r) => fmtDate(r.created_at), raw: (r) => fmtDate(r.created_at) },
    { key: "customer_name", label: "Customer", render: (r) => <span className="font-semibold text-slate-900">{r.customer_name}</span> },
    { key: "customer_phone", label: "Telepon", mono: true },
    { key: "pic_name", label: "PIC", render: (r) => r.pic_name ? <span className="text-slate-700" data-testid={`pic-${r.id.slice(0, 8)}`}>{r.pic_name}</span> : <span className="text-slate-300">—</span>, raw: (r) => r.pic_name || "" },
    { key: "items", label: "Items", sortable: false, render: (r) => <span className="text-slate-600">{r.items.length} item · {r.items.reduce((a, i) => a + i.qty, 0)} pcs</span>, raw: (r) => r.items.map((i) => `${i.name} x${i.qty}`).join("; ") },
    { key: "total", label: "Total", mono: true, render: (r) => <span className="font-semibold">{fmtIDR(r.total)}</span>, raw: (r) => r.total },
    { key: "payment_status", label: "Pembayaran", render: (r) => <StatusBadge map={PAYMENT_STATUS} value={r.payment_status} testId="badge-status-payment" />, raw: (r) => r.payment_status },
    { key: "status", label: "Status Order", render: (r) => <StatusBadge map={ORDER_STATUS} value={r.status} testId="badge-status-order" />, raw: (r) => r.status },
    { key: "actions", label: "", sortable: false, noExport: true, render: (r) => canOrder && (
      <div className="flex items-center gap-1">
        <Button size="sm" variant="outline" className="h-8" data-testid={`edit-order-${r.id.slice(0,8)}`} onClick={() => openEdit(r)}>Edit</Button>
        <button data-testid={`delete-order-${r.id.slice(0,8)}`} title="Hapus order" className="p-1.5 text-rose-400 hover:text-rose-600" onClick={() => remove(r)}><Trash2 className="w-4 h-4" /></button>
      </div>
    ) },
  ];

  const catName = (id) => catalog.find((c) => c.id === id);
  const itemsTotal = lines.reduce((a, l) => a + (catName(l.catalog_item_id)?.sell_price || 0) * Number(l.qty || 0), 0);
  const feesTotal = fees.reduce((a, f) => a + (Number(f.amount) || 0), 0);

  return (
    <div>
      <PageHeader title="Orders" subtitle="Sales input order. Edit order = status kembali ke menunggu pembayaran. Harga jual hanya finance.">
        {canOrder && <Button data-testid="btn-add-order" onClick={openNew} className="gap-1.5 bg-slate-900 hover:bg-slate-800"><Plus className="w-4 h-4" /> Buat Order</Button>}
      </PageHeader>
      <DataTable testId="orders-table" columns={columns} data={orders} searchKeys={["customer_name", "customer_phone", "status", "payment_status"]} exportName={`orders-${event?.code}`} />

      {/* Create / edit order */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editId ? "Edit Order" : "Buat Order Baru"}</DialogTitle></DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
            <div><Label>Customer <span className="text-slate-400 font-normal">(cari nama / nomor HP)</span></Label>
              <div className="mt-1">
                <CustomerSearch customers={customers} value={customerId} onSelect={setCustomerId}
                  onCreated={(c) => setCustomers((prev) => [...prev, c])} />
              </div>
            </div>
            <div><Label>PIC / Admin yang menangani</Label>
              <Select value={picId} onValueChange={setPicId}>
                <SelectTrigger data-testid="order-pic"><SelectValue placeholder="Pilih PIC" /></SelectTrigger>
                <SelectContent>
                  {!team.some((t) => t.id === user.id) && <SelectItem value={user.id}>{user.name} (saya)</SelectItem>}
                  {team.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}{t.id === user.id ? " (saya)" : ""} · {t.role}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Item Order <span className="text-slate-400 font-normal">(ketik beberapa huruf SKU/brand/nama untuk mencari)</span></Label>
              <div className="space-y-2 mt-1">
                {lines.map((l, idx) => (
                  <div key={l.key || idx} className="flex items-center gap-2">
                    <ItemSearch items={catalog} value={l.catalog_item_id} testId={`order-item-search-${idx}`} onSelect={(v) => setLines(lines.map((x, i) => i === idx ? { ...x, catalog_item_id: v } : x))} />
                    <Input data-testid={`order-item-qty-${idx}`} type="number" min="1" value={l.qty} onChange={(e) => setLines(lines.map((x, i) => i === idx ? { ...x, qty: e.target.value } : x))} className="w-20" />
                    <button onClick={() => setLines(lines.filter((_, i) => i !== idx))} className="p-2 text-slate-400 hover:text-rose-600"><X className="w-4 h-4" /></button>
                  </div>
                ))}
                <Button data-testid="order-add-line" variant="outline" size="sm" onClick={() => setLines([...lines, { key: Date.now(), catalog_item_id: "", qty: 1 }])} className="gap-1"><Plus className="w-3.5 h-3.5" /> Tambah Baris</Button>
              </div>
            </div>
            <div>
              <Label>Biaya Lainnya <span className="text-slate-400 font-normal">(ongkir, packing, dll — tidak dihitung profit)</span></Label>
              <div className="space-y-2 mt-1">
                {fees.map((f, idx) => (
                  <div key={f.key} className="flex items-center gap-2">
                    <Input data-testid={`order-fee-name-${idx}`} value={f.name} placeholder="Nama biaya, mis. Ongkir" onChange={(e) => setFees(fees.map((x, i) => i === idx ? { ...x, name: e.target.value } : x))} className="flex-1" />
                    <Input data-testid={`order-fee-amount-${idx}`} type="number" min="0" step="1000" value={f.amount} placeholder="Nominal (IDR)" onChange={(e) => setFees(fees.map((x, i) => i === idx ? { ...x, amount: e.target.value } : x))} className="w-40 font-mono" />
                    <button data-testid={`order-fee-remove-${idx}`} onClick={() => setFees(fees.filter((_, i) => i !== idx))} className="p-2 text-slate-400 hover:text-rose-600"><X className="w-4 h-4" /></button>
                  </div>
                ))}
                <Button data-testid="order-add-fee" variant="outline" size="sm" onClick={() => setFees([...fees, { key: Date.now(), name: "", amount: "" }])} className="gap-1"><Plus className="w-3.5 h-3.5" /> Tambah Biaya</Button>
              </div>
            </div>
            <div className="text-right text-sm space-y-0.5">
              <div className="text-slate-500">Subtotal Item: {fmtIDR(itemsTotal)}{feesTotal > 0 && <> · Biaya Lainnya: {fmtIDR(feesTotal)}</>}</div>
              <div className="font-semibold" data-testid="order-estimate-total">Estimasi Total: {fmtIDR(itemsTotal + feesTotal)}</div>
            </div>
            <div><Label>Catatan</Label><Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Request khusus..." /></div>
          </div>
          <DialogFooter><Button data-testid="save-order" onClick={save} className="bg-slate-900 hover:bg-slate-800">Simpan Order</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail drawer */}
      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle className="font-mono">Order {detail?.id.slice(0, 8)} · {detail?.customer_name}</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-3 max-h-[60vh] overflow-y-auto">
              <div className="flex gap-2 items-center">
                <StatusBadge map={PAYMENT_STATUS} value={detail.payment_status} />
                <StatusBadge map={ORDER_STATUS} value={detail.status} />
                {detail.pic_name && <span className="ml-auto text-xs text-slate-500" data-testid="order-detail-pic">PIC: <span className="font-semibold text-slate-800">{detail.pic_name}</span></span>}
              </div>
              <table className="w-full text-sm">
                <thead><tr className="text-xs uppercase text-slate-500 border-b"><th className="text-left py-1">Item</th><th>Qty</th><th>Dibeli</th><th className="text-right">Harga</th><th>Status</th>{canPrice && <th></th>}</tr></thead>
                <tbody>
                  {detail.items.map((i, idx) => (
                    <tr key={`${i.catalog_item_id}-${idx}`} className="border-b border-slate-100">
                      <td className="py-2">{i.name}{i.has_change && <span className="ml-1 text-[10px] text-purple-600 font-semibold">•ada perubahan</span>}</td>
                      <td className="text-center font-mono">{i.qty}</td>
                      <td className="text-center font-mono">{i.bought_qty}</td>
                      <td className="text-right font-mono">{fmtIDR(i.sell_price)}</td>
                      <td className="text-center"><StatusBadge map={ITEM_STATUS} value={i.status} /></td>
                      {canPrice && <td className="text-right"><PriceEdit price={i.sell_price} onSave={(p) => updatePrice(detail.id, idx, p)} idx={idx} /></td>}
                    </tr>
                  ))}
                </tbody>
              </table>
              {detail.extra_fees?.length > 0 && (
                <div data-testid="order-detail-fees" className="text-sm border-t pt-2 space-y-1">
                  <div className="text-xs uppercase text-slate-500 font-semibold">Biaya Lainnya</div>
                  {detail.extra_fees.map((f, i) => <div key={`${f.name}-${i}`} className="flex justify-between"><span>{f.name}</span><span className="font-mono">{fmtIDR(f.amount)}</span></div>)}
                </div>
              )}
              <div className="text-right font-bold">Total: {fmtIDR(detail.total)}</div>
              {detail.note && <p className="text-sm text-slate-500">Catatan: {detail.note}</p>}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PriceEdit({ price, onSave, idx }) {
  const [v, setV] = useState(price);
  const [editing, setEditing] = useState(false);
  if (!editing) return <Button size="sm" variant="ghost" className="h-7 text-xs" data-testid={`edit-price-${idx}`} onClick={() => setEditing(true)}>Ubah Harga</Button>;
  return (
    <div className="flex items-center gap-1 justify-end">
      <Input type="number" value={v} onChange={(e) => setV(e.target.value)} className="w-28 h-7" data-testid={`price-input-${idx}`} />
      <Button size="sm" className="h-7" data-testid={`save-price-${idx}`} onClick={() => { onSave(v); setEditing(false); }}>OK</Button>
    </div>
  );
}
